import React, { useEffect, useState } from 'react';
import { TagIcon, PencilSquareIcon, CheckIcon, XMarkIcon } from 'components/icons';
import { pipelineApi } from '../../services/api';

interface SolicitationType {
  type: string | null;
  tier: number | null;
  confidence: number | null;
  signals: string[] | null;
  classifiedAt: string | null;
  source: 'auto' | 'user_corrected' | null;
  needsAiReview: boolean;
}

interface Props {
  opportunityId: string;
}

const TYPE_OPTIONS: Array<{ value: string; tier: number; label: string }> = [
  { value: 'simple_rfq_price_only', tier: 1, label: 'Simple RFQ (price-only)' },
  { value: 'best_value_rfq', tier: 1, label: 'Best-Value RFQ' },
  { value: 'sealed_bid_ifb', tier: 2, label: 'Sealed Bid (IFB)' },
  { value: 'simplified_rfp', tier: 3, label: 'Simplified RFP' },
  { value: 'full_rfp_best_value', tier: 3, label: 'Full RFP (Best Value)' },
  { value: 'full_rfp_lpta', tier: 3, label: 'Full RFP (LPTA)' },
  { value: 'combined_synopsis_solicitation', tier: 3, label: 'Combined Synopsis/Solicitation' },
  { value: 'ota_prototype', tier: 4, label: 'OTA Prototype' },
  { value: 'ota_production', tier: 4, label: 'OTA Production' },
  { value: 'baa', tier: 4, label: 'Broad Agency Announcement (BAA)' },
  { value: 'sources_sought', tier: 5, label: 'Sources Sought' },
  { value: 'rfi', tier: 5, label: 'Request for Information (RFI)' },
  { value: 'draft_rfp', tier: 5, label: 'Draft RFP' },
  { value: 'pre_solicitation_notice', tier: 5, label: 'Pre-solicitation Notice' },
  { value: 'idiq_task_order', tier: 6, label: 'IDIQ Task Order' },
  { value: 'bpa_call', tier: 6, label: 'BPA Call' },
  { value: 'award_notice', tier: 0, label: 'Award Notice' },
  { value: 'cancellation', tier: 0, label: 'Cancellation' },
  { value: 'justification_or_other', tier: 0, label: 'Justification / Other' },
];

const TIER_COLORS: Record<number, string> = {
  0: 'bg-surface-sunk text-content border-border',
  1: 'bg-blue-50 text-blue-700 border-blue-200',
  2: 'bg-purple-50 text-purple-700 border-purple-200',
  3: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  4: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  5: 'bg-amber-50 text-amber-700 border-amber-200',
  6: 'bg-teal-50 text-teal-700 border-teal-200',
};

const TIER_LABELS: Record<number, string> = {
  0: 'Non-bid',
  1: 'Quote',
  2: 'Bid',
  3: 'Proposal',
  4: 'Other Transaction',
  5: 'Pre-solicitation',
  6: 'Task Order',
};

export const SolicitationTypeBadge: React.FC<Props> = ({ opportunityId }) => {
  const [classification, setClassification] = useState<SolicitationType | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedType, setSelectedType] = useState<string>('');

  const load = () => {
    setLoading(true);
    pipelineApi
      .getSolicitationType(opportunityId)
      .then(res => setClassification(res.data?.data || null))
      .catch(() => setClassification(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  // Keyed on the id it loads for. `load` is redefined every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunityId]);

  if (loading) return null;
  if (!classification?.type) return null;

  const typeOption = TYPE_OPTIONS.find(o => o.value === classification.type);
  const label = typeOption?.label || classification.type;
  const tier = classification.tier ?? 0;
  const colorClass = TIER_COLORS[tier] || TIER_COLORS[0];
  const confidence = classification.confidence ?? 0;
  const isUserCorrected = classification.source === 'user_corrected';

  const save = async () => {
    if (!selectedType) return;
    const opt = TYPE_OPTIONS.find(o => o.value === selectedType);
    if (!opt) return;
    setSaving(true);
    try {
      await pipelineApi.setSolicitationType(opportunityId, opt.value, opt.tier);
      setEditing(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className={`rounded-lg border p-3 ${colorClass}`}>
        <div className="flex items-center gap-2 mb-2">
          <TagIcon className="h-4 w-4" />
          <span className="text-xs font-semibold uppercase tracking-wide">
            Set Solicitation Type
          </span>
        </div>
        <select
          value={selectedType || classification.type || ''}
          onChange={e => setSelectedType(e.target.value)}
          className="w-full text-sm rounded-md border-border-strong mb-2 bg-surface"
          disabled={saving}
        >
          {TYPE_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>
              T{o.tier} — {o.label}
            </option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <button
            onClick={save}
            disabled={saving}
            className="text-xs px-2.5 py-1 bg-brand-600 text-white rounded-md hover:bg-brand-700 inline-flex items-center gap-1 disabled:opacity-50"
          >
            <CheckIcon className="h-3 w-3" />
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            onClick={() => setEditing(false)}
            disabled={saving}
            className="text-xs px-2.5 py-1 bg-surface border border-border-strong text-content rounded-md hover:bg-surface-sunk inline-flex items-center gap-1"
          >
            <XMarkIcon className="h-3 w-3" />
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border p-3 flex items-start justify-between gap-3 ${colorClass}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <TagIcon className="h-4 w-4 flex-shrink-0" />
          <span className="text-[10px] font-semibold uppercase tracking-wide opacity-75">
            Tier {tier} · {TIER_LABELS[tier]}
          </span>
          {isUserCorrected && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-surface/60">
              user-set
            </span>
          )}
          {!isUserCorrected && classification.needsAiReview && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-surface/60">
              low confidence
            </span>
          )}
        </div>
        <p className="text-sm font-semibold mt-0.5">{label}</p>
        {!isUserCorrected && confidence > 0 && (
          <p className="text-[11px] opacity-70 mt-0.5">
            Auto-classified · {Math.round(confidence * 100)}% confident
          </p>
        )}
      </div>
      <button
        onClick={() => {
          setSelectedType(classification.type || '');
          setEditing(true);
        }}
        className="flex-shrink-0 text-xs px-2 py-1 rounded-md bg-surface/60 hover:bg-surface inline-flex items-center gap-1"
        title="Override the solicitation type"
      >
        <PencilSquareIcon className="h-3 w-3" />
        <span className="hidden sm:inline">Change</span>
      </button>
    </div>
  );
};
